import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import { DateTimeFormatterHelper } from 'mbs-ui-shared@utils/helpers/dateTimeFormatterHelper';
import moment from 'moment';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'mbs-date-time-format',
  templateUrl: './date-time-format.component.html',
  imports: [CommonModule, I18NextPipe, NgbTooltipModule]
})
export class DateTimeFormatComponent {
  protected auth = inject(AuthFacade);

  iconClass = input('ico ico-info-circle');
  container = input<string>();
  dateTimeFormat = input(mediumDateWithTimeMoment);
  hid = input<string>();
  placement = input('top-right auto');
  needWaitLoadTimezone = input(false);
  showIcon = input(false);
  showTooltip = input(false);
  isAgentTime = input<boolean>();
  hideAgentTime = input(false);
  showAgentTimeAsMain = input(false);
  agentUtcOffsetValue = input<number>();
  time = input(new Date().toISOString());
  defaultText = input('-');

  iconHover = output<boolean>();

  isNullTimezone = computed(() => isNil(this.agentUtcOffsetValue()));

  private agentUtcOffset$ = toObservable(this.agentUtcOffsetValue);

  private time$ = toObservable(this.time);
  private dateTimeFormat$ = toObservable(this.dateTimeFormat);
  private defaultText$ = toObservable(this.defaultText);
  private isAgentTime$ = toObservable(this.isAgentTime);

  private agentDiff$ = this.isAgentTime$.pipe(
    switchMap((isAgentTime) => (isAgentTime ? of(this.getAgentDiff(this.time())) : this.getAgentUtcOffsetValue()))
  );

  private providerDiff$ = this.auth.currentUser$.pipe(map((user) => user?.ProviderInfo?.TimeZoneOffset));

  timeData = toSignal(
    combineLatest([this.dateTimeFormat$, this.defaultText$, this.isAgentTime$]).pipe(
      switchMap(([dateTimeFormat, defaultText, isAgentTime]) =>
        DateTimeFormatterHelper.setTime(this.time$, this.agentDiff$, this.providerDiff$, defaultText, isAgentTime, dateTimeFormat)
      )
    )
  );

  displayedTime = computed(() => (this.showAgentTimeAsMain() ? this.timeData()?.agent?.time : this.timeData()?.provider?.time));

  protected getAgentUtcOffsetValue(): Observable<number | undefined> {
    return this.agentUtcOffset$;
  }

  protected getAgentDiff = (time: any): number => {
    const regex = /([0-9+])-([0-9]+)T([0-9]+):([0-9]+):([0-9]+)(.*):([0-9]+)/gm;
    const regex2 = /([0-9+])-([0-9]+)T([0-9]+):([0-9]+):([0-9]+).([0-9]?[0-9]?[0-9])(.*):([0-9]+)/gm;

    let result = regex.exec(time);
    if (!result || result?.length < 8) {
      const agentUtcOffset = this.agentUtcOffsetValue();
      return isNil(agentUtcOffset) ? moment(this.time()).utcOffset() : agentUtcOffset;
    }

    let hours = Number(result[6]);
    let minutes = Number(result[7]);

    if (hours !== 0 && !hours) {
      result = regex2.exec(time);
      hours = Number(result[7]);
      minutes = Number(result[8]);
    }

    return hours * 60 + (hours > 0 ? minutes : -minutes);
  };

  infoIconHoverHandler(onHover: boolean): void {
    this.iconHover.emit(onHover);
  }
}
